/** @format */

import React, { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { getOrderDetail, orderUpdate, confirmCodPayment } from "../../store/order/order.action";
import { useLocation, useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { colors } from "../../../util/SkeletonColor";
import { getSetting } from "../../store/setting/setting.action";
import { formatAmount } from "../../extra/functions";

const OrderDetail = () => {
	const { orderDetail } = useSelector((state) => state.order);
	const { setting } = useSelector((state) => state.setting);
	const { state } = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(true);

	const currency = setting?.currency?.symbol || "";

	useEffect(() => {
		dispatch(getSetting());
		dispatch(getOrderDetail(state?._id || state));
	}, [dispatch, state]);

	useEffect(() => {
		const timer = setTimeout(() => setLoading(false), 1500);
		return () => clearTimeout(timer);
	}, []);

	const formatDate = (dateStr) => {
		if (!dateStr) return "-";
		const d = new Date(dateStr);
		return d.toLocaleDateString("en-NG", { year: "numeric", month: "short", day: "numeric" });
	};

	const formatTime = (dateStr) => {
		if (!dateStr) return "-";
		const d = new Date(dateStr);
		return d.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit", hour12: true });
	};

	const getGatewayLabel = (gw) => {
		if (!gw) return "-";
		const map = { cod: "Cash on Delivery", paystack: "Paystack", stripe: "Stripe", flutterwave: "Flutterwave" };
		return map[gw.toLowerCase()] || gw;
	};

	const getStatusConfig = (status) => {
		if (!status) return { bg: "#f1f5f9", color: "#64748b", icon: "○" };
		switch (status.toLowerCase()) {
			case "delivered":
				return { bg: "#dcfce7", color: "#16a34a", icon: "✓" };
			case "pending":
				return { bg: "#fef9c3", color: "#a16207", icon: "◷" };
			case "confirmed":
				return { bg: "#dbeafe", color: "#2563eb", icon: "✓" };
			case "out for delivery":
			case "out_of_delivery":
			case "out of delivery":
				return { bg: "#e0f2fe", color: "#0284c7", icon: "→" };
			case "cancel":
			case "cancelled":
				return { bg: "#fee2e2", color: "#dc2626", icon: "✕" };
			default:
				return { bg: "#f1f5f9", color: "#64748b", icon: "○" };
		}
	};

	const getPaymentConfig = (status) => {
		switch (status) {
			case 2:
				return { label: "Paid", bg: "#dcfce7", color: "#16a34a" };
			case 3:
				return { label: "Cancelled", bg: "#fee2e2", color: "#dc2626" };
			case 4:
				return { label: "Refund", bg: "#ede9fe", color: "#7c3aed" };
			default:
				return { label: "Pending", bg: "#fef9c3", color: "#a16207" };
		}
	};

	const overallStatus = orderDetail?.items?.[0]?.status || "Pending";
	const statusConfig = getStatusConfig(overallStatus);
	const payConfig = getPaymentConfig(orderDetail?.paymentStatus);

	const SkeletonBlock = ({ height = 160 }) => (
		<div style={{ background: "#fff", borderRadius: "16px", padding: "24px", marginBottom: "16px", border: "1px solid #f1f5f9" }}>
			<Skeleton height={16} width='35%' className='mb-3' baseColor={colors?.baseColor} highlightColor={colors?.highlightColor} />
			<Skeleton height={height - 40} borderRadius={12} baseColor={colors?.baseColor} highlightColor={colors?.highlightColor} />
		</div>
	);

	return (
		<div style={{ maxWidth: "1360px", margin: "0 auto", padding: "32px 24px", fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
			{/* Top Bar */}
			<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "32px" }}>
				<button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", color: "#64748b", fontSize: "14px", fontWeight: 500, padding: "8px 0", transition: "color 0.2s" }} onMouseEnter={(e) => (e.currentTarget.style.color = "#0f172a")} onMouseLeave={(e) => (e.currentTarget.style.color = "#64748b")}>
					<svg width='20' height='20' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' viewBox='0 0 24 24'>
						<path d='M19 12H5M12 19l-7-7 7-7' />
					</svg>
					Back to Orders
				</button>
			</div>

			{/* Order Header Card */}
			<div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", padding: "28px 32px", marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "20px" }}>
				<div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
					<div>
						<div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
							<h3 style={{ margin: 0, fontSize: "24px", fontWeight: 700, color: "#0f172a", letterSpacing: "-0.5px" }}>{orderDetail?.orderId || ""}</h3>
							<span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "5px 14px", borderRadius: "24px", fontSize: "12px", fontWeight: 600, backgroundColor: statusConfig.bg, color: statusConfig.color, textTransform: "capitalize" }}>
								<span style={{ fontSize: "10px" }}>{statusConfig.icon}</span> {overallStatus}
							</span>
						</div>
						<div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
							<span style={{ fontSize: "13px", color: "#94a3b8", display: "flex", alignItems: "center", gap: "6px" }}>
								<svg width='14' height='14' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
									<rect x='3' y='4' width='18' height='18' rx='2' />
									<path d='M16 2v4M8 2v4M3 10h18' />
								</svg>
								{formatDate(orderDetail?.createdAt)} at {formatTime(orderDetail?.createdAt)}
							</span>
							<span style={{ fontSize: "13px", color: "#94a3b8", display: "flex", alignItems: "center", gap: "6px" }}>
								<svg width='14' height='14' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
									<path d='M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z' />
								</svg>
								{orderDetail?.totalItems || 0} item{orderDetail?.totalItems !== 1 ? "s" : ""} &middot; Qty {orderDetail?.totalQuantity || 0}
							</span>
						</div>
					</div>
				</div>
				<div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
					<div style={{ textAlign: "right" }}>
						<div style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 500, marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Total</div>
						<div style={{ fontSize: "28px", fontWeight: 800, color: "#0f172a", letterSpacing: "-1px" }}>
							{currency}
							{formatAmount(orderDetail?.finalTotal)}
						</div>
					</div>
				</div>
			</div>

			{/* Stats Row */}
			<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" }}>
				{/* Customer quick card */}
				{orderDetail?.userId && (
					<div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e2e8f0", padding: "20px", display: "flex", alignItems: "center", gap: "14px" }}>
						<div style={{ width: "44px", height: "44px", borderRadius: "12px", background: "linear-gradient(135deg, #e54a13 0%, #f97316 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: 700, flexShrink: 0 }}>{(orderDetail.userId?.firstName || "?")[0]?.toUpperCase()}</div>
						<div style={{ minWidth: 0 }}>
							<div style={{ fontWeight: 600, fontSize: "14px", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
								{orderDetail.userId?.firstName || ""} {orderDetail.userId?.lastName || ""}
							</div>
							<div style={{ fontSize: "12px", color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{orderDetail.userId?.email || ""}</div>
							{orderDetail.userId?.mobileNumber && <div style={{ fontSize: "12px", color: "#94a3b8" }}>{orderDetail.userId.mobileNumber}</div>}
						</div>
					</div>
				)}
				{/* Payment quick card */}
				<div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e2e8f0", padding: "20px", display: "flex", alignItems: "center", gap: "14px" }}>
					<div style={{ width: "44px", height: "44px", borderRadius: "12px", backgroundColor: payConfig.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
						<svg width='20' height='20' fill='none' stroke={payConfig.color} strokeWidth='2' viewBox='0 0 24 24'>
							<rect x='1' y='4' width='22' height='16' rx='2' />
							<path d='M1 10h22' />
						</svg>
					</div>
					<div>
						<div style={{ fontWeight: 600, fontSize: "14px", color: "#0f172a" }}>{getGatewayLabel(orderDetail?.paymentGateway)}</div>
						<span style={{ display: "inline-block", padding: "2px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, backgroundColor: payConfig.bg, color: payConfig.color, marginTop: "4px" }}>{payConfig.label}</span>
					</div>
				</div>
				{/* Shipping quick card */}
				<div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e2e8f0", padding: "20px", display: "flex", alignItems: "center", gap: "14px" }}>
					<div style={{ width: "44px", height: "44px", borderRadius: "12px", backgroundColor: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
						<svg width='20' height='20' fill='none' stroke='#3b82f6' strokeWidth='2' viewBox='0 0 24 24'>
							<path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z' />
							<circle cx='12' cy='10' r='3' />
						</svg>
					</div>
					<div style={{ minWidth: 0 }}>
						<div style={{ fontWeight: 600, fontSize: "14px", color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{orderDetail?.shippingAddress?.city || orderDetail?.shippingAddress?.state || "N/A"}</div>
						<div style={{ fontSize: "12px", color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{orderDetail?.shippingAddress?.address || "No address"}</div>
					</div>
				</div>
			</div>

			<div className='row'>
				{/* LEFT COLUMN */}
				<div className='col-12 col-lg-8' style={{ marginBottom: "24px" }}>
					{/* Order Items */}
					<div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
						<div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
							<h6 style={{ margin: 0, fontSize: "15px", fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
								<svg width='18' height='18' fill='none' stroke='#e54a13' strokeWidth='2' viewBox='0 0 24 24'>
									<path d='M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0' />
								</svg>
								Items
							</h6>
							<span style={{ fontSize: "12px", color: "#94a3b8", fontWeight: 500 }}>
								{orderDetail?.items?.length || 0} product{(orderDetail?.items?.length || 0) !== 1 ? "s" : ""}
							</span>
						</div>

						{loading ? (
							<div style={{ padding: "20px" }}>
								<SkeletonBlock />
							</div>
						) : (
							<div style={{ padding: "8px" }}>
								{orderDetail?.items?.map((item, idx) => {
									const itemStatus = getStatusConfig(item.status);
									return (
										<div key={idx} style={{ display: "flex", gap: "16px", padding: "16px", borderRadius: "12px", margin: "4px 0", transition: "background 0.15s", cursor: "default", flexWrap: "wrap" }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#fafbfc")} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}>
											{/* Product Image */}
											<div style={{ width: "80px", height: "100px", borderRadius: "12px", overflow: "hidden", flexShrink: 0, backgroundColor: "#f8fafc", border: "1px solid #f1f5f9" }}>
												<img
													src={item.productId?.mainImage}
													alt={item.productId?.productName || "Product"}
													style={{ width: "100%", height: "100%", objectFit: "cover" }}
													onError={(e) => {
														e.target.style.display = "none";
													}}
												/>
											</div>

											{/* Product Info */}
											<div style={{ flex: 1, minWidth: "200px" }}>
												<div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", marginBottom: "6px" }}>
													<div style={{ fontSize: "15px", fontWeight: 600, color: "#0f172a" }}>{item.productId?.productName || "-"}</div>
													<span style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, backgroundColor: itemStatus.bg, color: itemStatus.color, textTransform: "capitalize", whiteSpace: "nowrap", flexShrink: 0 }}>
														<span style={{ fontSize: "9px" }}>{itemStatus.icon}</span> {item.status || "Pending"}
													</span>
												</div>

												<div style={{ display: "flex", flexWrap: "wrap", gap: "16px", fontSize: "13px", color: "#64748b", marginBottom: "8px" }}>
													<span>
														Seller:{" "}
														{item?.sellerId?._id ? (
															<strong onClick={() => navigate("/admin/sellerProfile", { state: item.sellerId._id })} style={{ color: "#e54a13", fontWeight: 600, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }} title='View seller profile'>
																{(item?.sellerId?.firstName || "-") + " " + (item?.sellerId?.lastName || "")}
															</strong>
														) : (
															<strong style={{ color: "#374151", fontWeight: 500 }}>{(item?.sellerId?.firstName || "-") + " " + (item?.sellerId?.lastName || "")}</strong>
														)}
													</span>
													<span style={{ color: "#e2e8f0" }}>|</span>
													<span style={{ fontFamily: "monospace", fontSize: "12px", color: "#94a3b8" }}>{item?.productCode || "-"}</span>
												</div>

												<div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
													<div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 12px", borderRadius: "8px", backgroundColor: "#f8fafc", fontSize: "13px" }}>
														<span style={{ color: "#94a3b8" }}>Qty</span>
														<span style={{ fontWeight: 600, color: "#0f172a" }}>{item?.productQuantity || 0}</span>
													</div>
													{item?.purchasedTimeProductPrice > 0 && (
														<div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 12px", borderRadius: "8px", backgroundColor: "#f8fafc", fontSize: "13px" }}>
															<span style={{ color: "#94a3b8" }}>Price</span>
															<span style={{ fontWeight: 600, color: "#0f172a" }}>
																{currency}
																{formatAmount(item.purchasedTimeProductPrice)}
															</span>
														</div>
													)}
													{item?.purchasedTimeShippingCharges > 0 && (
														<div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "4px 12px", borderRadius: "8px", backgroundColor: "#f0fdf4", fontSize: "13px" }}>
															<span style={{ color: "#94a3b8" }}>Ship</span>
															<span style={{ fontWeight: 600, color: "#16a34a" }}>
																{currency}
																{formatAmount(item.purchasedTimeShippingCharges)}
															</span>
														</div>
													)}
												</div>

												{/* Attributes */}
												{(() => {
													const extractAttrValue = (att) => {
														const toStr = (v) => (typeof v === "object" && v !== null ? v.value || v.name || Object.values(v)[0] || "" : v == null ? "" : String(v));
														if (Array.isArray(att?.values) && att.values.length > 0) {
															return att.values.map(toStr).filter(Boolean).join(", ");
														}
														return toStr(att?.value);
													};
													const chips = (item.attributesArray || []).map((att, i) => ({ key: i, name: att?.name, value: extractAttrValue(att) })).filter((c) => c.name && c.value);
													if (chips.length === 0) return null;
													return (
														<div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
															{chips.map((c) => (
																<span key={c.key} style={{ display: "inline-block", padding: "3px 10px", borderRadius: "6px", fontSize: "11px", fontWeight: 500, backgroundColor: "#eef2ff", color: "#4338ca", border: "1px solid #e0e7ff" }}>
																	{c.name}: {c.value}
																</span>
															))}
														</div>
													);
												})()}

												{/* Tracking inline */}
												{item?.trackingLink && (
													<a href={item.trackingLink} target='_blank' rel='noopener noreferrer' style={{ display: "inline-flex", alignItems: "center", gap: "6px", marginTop: "10px", fontSize: "12px", color: "#e54a13", fontWeight: 600, textDecoration: "none" }}>
														<svg width='14' height='14' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
															<path d='M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3' />
														</svg>
														{item.trackingId || "Track Package"}
													</a>
												)}
											</div>
										</div>
									);
								})}
							</div>
						)}
					</div>
				</div>

				{/* RIGHT COLUMN */}
				<div className='col-12 col-lg-4' style={{ marginBottom: "24px" }}>
					{loading ? (
						<>
							<SkeletonBlock height={120} />
							<SkeletonBlock height={100} />
						</>
					) : (
						<div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
							{/* Price Breakdown */}
							<div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
								<div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9" }}>
									<h6 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
										<svg width='16' height='16' fill='none' stroke='#e54a13' strokeWidth='2' viewBox='0 0 24 24'>
											<path d='M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6' />
										</svg>
										Price Breakdown
									</h6>
								</div>
								<div style={{ padding: "20px 24px" }}>
									<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" }}>
										<span style={{ color: "#64748b" }}>Subtotal</span>
										<span style={{ fontWeight: 600, color: "#0f172a" }}>
											{currency}
											{formatAmount(orderDetail?.subTotal)}
										</span>
									</div>
									{(orderDetail?.discount || 0) > 0 && (
										<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" }}>
											<span style={{ color: "#64748b" }}>Discount</span>
											<span style={{ fontWeight: 600, color: "#dc2626" }}>
												-{currency}
												{formatAmount(orderDetail?.discount)}
											</span>
										</div>
									)}
									<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "14px" }}>
										<span style={{ color: "#64748b" }}>Shipping</span>
										<span style={{ fontWeight: 600, color: "#16a34a" }}>
											+{currency}
											{formatAmount(orderDetail?.totalShippingCharges || 0)}
										</span>
									</div>
									<div style={{ display: "flex", justifyContent: "space-between", padding: "16px 0 4px", borderTop: "1px dashed #e2e8f0", marginTop: "8px" }}>
										<span style={{ fontSize: "14px", fontWeight: 700, color: "#0f172a" }}>Total</span>
										<span style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px" }}>
											{currency}
											{formatAmount(orderDetail?.finalTotal)}
										</span>
									</div>
								</div>
							</div>

							{/* Payment Details */}
							<div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
								<div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9" }}>
									<h6 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
										<svg width='16' height='16' fill='none' stroke='#e54a13' strokeWidth='2' viewBox='0 0 24 24'>
											<rect x='1' y='4' width='22' height='16' rx='2' />
											<path d='M1 10h22' />
										</svg>
										Payment
									</h6>
								</div>
								<div style={{ padding: "20px 24px" }}>
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
										<span style={{ fontSize: "13px", color: "#94a3b8" }}>Method</span>
										<span style={{ fontSize: "14px", fontWeight: 600, color: "#0f172a" }}>{getGatewayLabel(orderDetail?.paymentGateway)}</span>
									</div>
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
										<span style={{ fontSize: "13px", color: "#94a3b8" }}>Status</span>
										<span style={{ display: "inline-block", padding: "3px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, backgroundColor: payConfig.bg, color: payConfig.color }}>{payConfig.label}</span>
									</div>
									{orderDetail?.paymentReference && (
										<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
											<span style={{ fontSize: "13px", color: "#94a3b8" }}>Reference</span>
											<span style={{ fontSize: "12px", fontWeight: 500, color: "#64748b", fontFamily: "monospace", maxWidth: "55%", wordBreak: "break-all", textAlign: "right" }}>{orderDetail.paymentReference}</span>
										</div>
									)}
									{orderDetail?.paymentStatus === 1 && !["Cancelled"].includes(orderDetail?.items?.[0]?.status) && (
										<button
											style={{ width: "100%", marginTop: "16px", padding: "12px", border: "none", borderRadius: "10px", fontWeight: 600, fontSize: "13px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", background: "linear-gradient(135deg, #059669 0%, #10b981 100%)", color: "#fff", transition: "opacity 0.2s" }}
											onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
											onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
											onClick={() => {
												if (window.confirm("Are you sure you want to mark this order as paid?")) {
													const firstItem = orderDetail?.items?.[0];
													if (firstItem) {
														dispatch(confirmCodPayment(orderDetail?.userId?._id || orderDetail?.userId, orderDetail?._id, firstItem._id));
													}
												}
											}}>
											<svg width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2.5' viewBox='0 0 24 24'>
												<path d='M20 6L9 17l-5-5' />
											</svg>
											Confirm Payment
										</button>
									)}
								</div>
							</div>

							{/* Shipping Address */}
							<div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
								<div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9" }}>
									<h6 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
										<svg width='16' height='16' fill='none' stroke='#e54a13' strokeWidth='2' viewBox='0 0 24 24'>
											<path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z' />
											<circle cx='12' cy='10' r='3' />
										</svg>
										Shipping Address
									</h6>
								</div>
								<div style={{ padding: "20px 24px" }}>
									{orderDetail?.shippingAddress ? (
										<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
											<div style={{ fontWeight: 600, fontSize: "14px", color: "#0f172a" }}>{orderDetail.shippingAddress.name || "-"}</div>
											<div style={{ fontSize: "13px", color: "#64748b", lineHeight: "1.6" }}>
												{orderDetail.shippingAddress.address && <div>{orderDetail.shippingAddress.address}</div>}
												<div>
													{[orderDetail.shippingAddress.city, orderDetail.shippingAddress.state].filter(Boolean).join(", ")}
													{orderDetail.shippingAddress.zipCode ? ` ${orderDetail.shippingAddress.zipCode}` : ""}
												</div>
												{orderDetail.shippingAddress.country && <div>{orderDetail.shippingAddress.country}</div>}
											</div>
										</div>
									) : (
										<div style={{ color: "#94a3b8", fontSize: "13px" }}>No shipping address</div>
									)}
								</div>
							</div>

							{/* Logistics */}
							{(() => {
								const firstItem = orderDetail?.items?.[0];
								const provider = (orderDetail?.logisticsProvider || firstItem?.logisticsProvider || "").toString();
								const isKos = /kos/i.test(provider);
								const providerLabel = provider ? (isKos ? `${provider.toUpperCase()} Logistics` : provider) : "Seller Ships";
								const shipmentType = orderDetail?.shipmentType || firstItem?.shipmentType;
								const hasWaybill = !!firstItem?.waybillNumber;
								return (
									<div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
										<div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9" }}>
											<h6 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
												<svg width='16' height='16' fill='none' stroke='#e54a13' strokeWidth='2' viewBox='0 0 24 24'>
													<rect x='1' y='3' width='15' height='13' />
													<path d='M16 8h4l3 3v5h-7V8zM5.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM18.5 21a2.5 2.5 0 100-5 2.5 2.5 0 000 5z' />
												</svg>
												Delivery
											</h6>
										</div>
										<div style={{ padding: "20px 24px" }}>
											<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", borderRadius: "10px", backgroundColor: isKos ? "#f0fdf4" : "#fff7ed", marginBottom: "16px" }}>
												<svg width='16' height='16' fill='none' stroke={isKos ? "#16a34a" : "#e54a13"} strokeWidth='2.5' viewBox='0 0 24 24'>
													<path d='M20 6L9 17l-5-5' />
												</svg>
												<span style={{ fontSize: "12px", fontWeight: 700, color: isKos ? "#16a34a" : "#e54a13" }}>{providerLabel}</span>
											</div>
											{shipmentType && (
												<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "13px" }}>
													<span style={{ color: "#94a3b8" }}>Shipment Type</span>
													<span style={{ fontWeight: 600, color: "#0f172a" }}>{shipmentType}</span>
												</div>
											)}
											{isKos && (firstItem?.dropOffLocationName || firstItem?.dropOffLocationAddress || firstItem?.dropOffLocationPhone) && (
												<div style={{ marginTop: "10px", padding: "12px 14px", borderRadius: "10px", backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
													<div style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "8px" }}>Drop-off Location</div>
													{firstItem.dropOffLocationName && <div style={{ fontSize: "13px", fontWeight: 600, color: "#0f172a", marginBottom: "4px" }}>{firstItem.dropOffLocationName}</div>}
													{firstItem.dropOffLocationAddress && <div style={{ fontSize: "12px", color: "#64748b", lineHeight: "1.5", marginBottom: "4px" }}>{firstItem.dropOffLocationAddress}</div>}
													{firstItem.dropOffLocationPhone && <div style={{ fontSize: "12px", color: "#64748b" }}>📞 {firstItem.dropOffLocationPhone}</div>}
												</div>
											)}
											{hasWaybill ? (
												<>
													<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 14px", borderRadius: "10px", backgroundColor: "#f0fdf4", marginBottom: "16px" }}>
														<svg width='16' height='16' fill='none' stroke='#16a34a' strokeWidth='2.5' viewBox='0 0 24 24'>
															<path d='M20 6L9 17l-5-5' />
														</svg>
														<span style={{ fontSize: "12px", fontWeight: 600, color: "#16a34a" }}>Managed by {firstItem.logisticsProvider || "KOS"}</span>
													</div>
													<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "13px" }}>
														<span style={{ color: "#94a3b8" }}>Waybill</span>
														<span style={{ fontWeight: 600, color: "#0f172a", fontFamily: "monospace", fontSize: "12px" }}>{firstItem.waybillNumber}</span>
													</div>
													{firstItem.logisticsShippingFee > 0 && (
														<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "13px" }}>
															<span style={{ color: "#94a3b8" }}>Fee</span>
															<span style={{ fontWeight: 600, color: "#16a34a" }}>
																{currency}
																{formatAmount(firstItem.logisticsShippingFee)}
															</span>
														</div>
													)}
													{firstItem.waybillPdfUrl && (
														<a href={firstItem.waybillPdfUrl} target='_blank' rel='noopener noreferrer' style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "14px", padding: "10px", borderRadius: "10px", backgroundColor: "#fff7ed", border: "1px solid #fed7aa", color: "#e54a13", fontWeight: 600, fontSize: "13px", textDecoration: "none", transition: "background 0.2s" }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#ffedd5")} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff7ed")}>
															<svg width='14' height='14' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
																<path d='M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z' />
																<path d='M14 2v6h6M16 13H8M16 17H8M10 9H8' />
															</svg>
															Download Waybill PDF
														</a>
													)}
												</>
											) : (
												<>
													{firstItem?.trackingId && (
														<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "13px" }}>
															<span style={{ color: "#94a3b8" }}>Tracking ID</span>
															<span style={{ fontWeight: 600, color: "#0f172a", fontFamily: "monospace", fontSize: "12px" }}>{firstItem.trackingId}</span>
														</div>
													)}
													{firstItem?.deliveredServiceName && (
														<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "13px" }}>
															<span style={{ color: "#94a3b8" }}>Carrier</span>
															<span style={{ fontWeight: 600, color: "#0f172a" }}>{firstItem.deliveredServiceName}</span>
														</div>
													)}
													{firstItem?.trackingLink && (
														<a href={firstItem.trackingLink} target='_blank' rel='noopener noreferrer' style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "14px", padding: "10px", borderRadius: "10px", backgroundColor: "#fff7ed", border: "1px solid #fed7aa", color: "#e54a13", fontWeight: 600, fontSize: "13px", textDecoration: "none" }}>
															<svg width='14' height='14' fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24'>
																<path d='M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3' />
															</svg>
															Track Package
														</a>
													)}
												</>
											)}
										</div>
									</div>
								);
							})()}

							{/* Promo Code */}
							{orderDetail?.promoCode?.promoCode && (
								<div style={{ background: "#fff", borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
									<div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9" }}>
										<h6 style={{ margin: 0, fontSize: "14px", fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
											<svg width='16' height='16' fill='none' stroke='#e54a13' strokeWidth='2' viewBox='0 0 24 24'>
												<path d='M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z' />
												<circle cx='7' cy='7' r='1' />
											</svg>
											Promo Code
										</h6>
									</div>
									<div style={{ padding: "20px 24px" }}>
										<div style={{ display: "inline-block", padding: "6px 16px", borderRadius: "8px", fontSize: "14px", fontWeight: 700, backgroundColor: "#f0fdf4", color: "#16a34a", border: "1px dashed #86efac", letterSpacing: "1px", marginBottom: "14px" }}>{orderDetail.promoCode.promoCode}</div>
										<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "13px" }}>
											<span style={{ color: "#94a3b8" }}>Discount</span>
											<span style={{ fontWeight: 600, color: "#dc2626" }}>{orderDetail.promoCode.discountType === 1 ? `${orderDetail.promoCode.discountAmount}%` : `${currency}${formatAmount(orderDetail.promoCode.discountAmount)}`}</span>
										</div>
										<div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontSize: "13px" }}>
											<span style={{ color: "#94a3b8" }}>Type</span>
											<span style={{ fontWeight: 600, color: "#0f172a" }}>{orderDetail.promoCode.discountType === 1 ? "Percentage" : "Flat"}</span>
										</div>
									</div>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default connect(null, { getOrderDetail, orderUpdate, confirmCodPayment })(OrderDetail);
